import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const NotificationContext = createContext(null);

// Convert urlsafe-b64 (no padding) → Uint8Array, required by PushManager.subscribe.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function setupServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    return reg;
  } catch (e) {
    console.warn("SW registration failed:", e);
    return null;
  }
}

async function requestPermission() {
  if (!("Notification" in window)) return "denied";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  return await Notification.requestPermission();
}

function setBadge(swReg, count) {
  try {
    if (navigator.setAppBadge) {
      if (count > 0) navigator.setAppBadge(count).catch((e) => console.warn("setAppBadge:", e));
      else navigator.clearAppBadge?.().catch((e) => console.warn("clearAppBadge:", e));
    }
  } catch (e) {
    console.warn("Badge API failed:", e);
  }
}

// Subscribe the current device to Web Push and report the subscription to the backend.
// Idempotent — repeat calls just refresh the stored subscription. Silently no-ops if
// the browser doesn't support Push (e.g. Safari outside an installed PWA on iOS < 16.4).
async function registerWebPushSubscription(swReg) {
  if (!swReg || !("PushManager" in window)) return null;
  try {
    const { data: { public_key } } = await api.get("/push/vapid-public-key");
    if (!public_key) return null;
    let sub = await swReg.pushManager.getSubscription();
    if (sub) {
      const existingKey = sub.options?.applicationServerKey;
      if (existingKey) {
        const existingB64 = btoa(String.fromCharCode(...new Uint8Array(existingKey)))
          .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
        if (existingB64 !== public_key) {
          await sub.unsubscribe();
          sub = null;
        }
      }
    }
    if (!sub) {
      sub = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
    return sub;
  } catch (e) {
    console.warn("Web Push subscribe failed:", e);
    return null;
  }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [permissionState, setPermissionState] = useState("default");
  const swRegRef = useRef(null);
  const pushRegisteredRef = useRef(false);
  // Reactive mirror of pushRegisteredRef so UI can show subscription status.
  const [pushSubscribed, setPushSubscribed] = useState(false);

  useEffect(() => {
    setupServiceWorker().then((reg) => {
      swRegRef.current = reg;
    });
    if ("Notification" in window) {
      setPermissionState(Notification.permission);
    }
  }, []);

  // When the user logs in AND they've already granted permission, subscribe to Web Push.
  useEffect(() => {
    if (!user) {
      pushRegisteredRef.current = false;
      setPushSubscribed(false);
      return;
    }
    if (Notification.permission !== "granted") return;
    if (pushRegisteredRef.current) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 20 && !swRegRef.current; i++) {
        await new Promise((r) => setTimeout(r, 100));
      }
      if (cancelled || !swRegRef.current) return;
      const sub = await registerWebPushSubscription(swRegRef.current);
      if (sub) {
        pushRegisteredRef.current = true;
        setPushSubscribed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Ensure this device is subscribed — call any time you need to refresh state
  // (e.g. when the user opens the notification bell on a device that didn't
  // pick up a subscription earlier, or after Reset notifications). Idempotent.
  const ensurePushSubscription = useCallback(async () => {
    if (!user) return false;
    if (Notification.permission !== "granted") return false;
    if (!swRegRef.current) {
      for (let i = 0; i < 20 && !swRegRef.current; i++) {
        await new Promise((r) => setTimeout(r, 100));
      }
    }
    if (!swRegRef.current) return false;
    const sub = await registerWebPushSubscription(swRegRef.current);
    if (sub) {
      pushRegisteredRef.current = true;
      setPushSubscribed(true);
      return true;
    }
    return false;
  }, [user]);

  // Poll for notifications when user is logged in. Note: this ONLY updates the
  // bell badge and the in-app notification list — it never fires a local OS
  // "toast" notification. Server-side Web Push is the single source of truth
  // for OS popups. This eliminates the entire class of duplicate-popup bugs
  // where the same event would fire once via Web Push and again via an in-tab
  // dispatch through the service worker.
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications");
      const items = data.notifications || [];
      const unread = data.unread_count || 0;
      setNotifications(items);
      setUnreadCount(unread);
      setBadge(swRegRef.current, unread);
    } catch (e) {
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Notifications fetch failed:", e);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setBadge(swRegRef.current, 0);
      return;
    }
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [user, fetchNotifications]);

  const requestNotificationPermission = async () => {
    const result = await requestPermission();
    setPermissionState(result);
    if (result === "granted" && swRegRef.current) {
      const sub = await registerWebPushSubscription(swRegRef.current);
      if (sub) {
        pushRegisteredRef.current = true;
        setPushSubscribed(true);
      }
    }
    return result;
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      setBadge(swRegRef.current, Math.max(0, unreadCount - 1));
    } catch (e) { console.error(e); }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      setBadge(swRegRef.current, 0);
    } catch (e) { console.error(e); }
  };

  /** Wipe every push subscription for THIS user across all devices, unsubscribe
   * this device's local PushManager, and re-subscribe with a fresh endpoint.
   * The "Reset notifications" button in Settings calls this — use it if duplicate
   * pushes ever appear due to a stale endpoint left behind by a previous device. */
  const resetPushSubscriptions = async () => {
    try {
      await api.post("/push/clear-mine");
      pushRegisteredRef.current = false;
      setPushSubscribed(false);
      if (swRegRef.current) {
        const existing = await swRegRef.current.pushManager.getSubscription();
        if (existing) await existing.unsubscribe();
        const sub = await registerWebPushSubscription(swRegRef.current);
        if (sub) {
          pushRegisteredRef.current = true;
          setPushSubscribed(true);
        }
      }
      return { success: true };
    } catch (e) {
      console.warn("resetPushSubscriptions failed:", e);
      return { success: false, error: e?.response?.data?.detail || String(e) };
    }
  };

  return (
    <NotificationContext.Provider value={{
      notifications, unreadCount, permissionState, pushSubscribed,
      requestNotificationPermission, ensurePushSubscription,
      markAsRead, markAllRead, fetchNotifications,
      resetPushSubscriptions,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be inside NotificationProvider");
  return ctx;
}
